import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Search, MessageSquare, Calendar, Edit2, Trash2, GitMerge, PhoneCall, Layers } from "lucide-react";
import { formatDistanceToNow, formatDistance } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { callGibletApi } from "@/services/gibletService";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Conversation {
  id: string;
  title: string;
  giblet_variant: string;
  last_message_at: string;
  created_at: string;
  message_count?: number;
  metadata?: {
    is_call_thread?: boolean;
    is_merged?: boolean;
    is_source_thread?: boolean;
    merged_into_thread_id?: string;
    merge_date?: string;
    call_type?: string;
    bounty_amount?: number;
    source_threads?: string[];
    thread_colors?: Array<{ title: string; color: string }>;
  };
}

const THREAD_COLORS = [
  '#000000', // Black
  '#1E40AF', // Blue
  '#DC2626', // Red
  '#059669', // Green
  '#7C3AED', // Purple
  '#EA580C', // Dark Orange
  '#0891B2', // Cyan
  '#BE123C', // Rose
];

interface GibletChatHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  currentVariant: string;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
}

export const GibletChatHistory = ({
  open,
  onOpenChange,
  userId,
  currentVariant,
  onSelectConversation,
  onNewConversation,
}: GibletChatHistoryProps) => {
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState<string[]>([]);
  const [showMergePreview, setShowMergePreview] = useState(false);
  const [mergePreviewData, setMergePreviewData] = useState<any>(null);
  const [mergedThreadTitle, setMergedThreadTitle] = useState("");
  const [mergingInProgress, setMergingInProgress] = useState(false);

  useEffect(() => {
    if (open && userId) {
      loadConversations();
    }
  }, [open, userId, currentVariant]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('giblet_conversations')
        .select(`
          id,
          title,
          giblet_variant,
          last_message_at,
          created_at,
          metadata
        `)
        .eq('user_id', userId)
        .eq('giblet_variant', currentVariant)
        .order('last_message_at', { ascending: false });

      if (error) throw error;

      // Get message counts for each conversation
      const conversationsWithCounts = await Promise.all(
        (data || []).map(async (conv) => {
          const { count } = await supabase
            .from('giblet_chat_messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id);
          
          return { 
            ...conv, 
            message_count: count || 0,
            metadata: conv.metadata as any
          };
        })
      );

      setConversations(conversationsWithCounts);
    } catch (error: any) {
      console.error('Error loading conversations:', error);
      toast({
        title: "Failed to load conversations",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleThreadSelect = (threadId: string) => {
    setSelectedThreads(prev => 
      prev.includes(threadId) 
        ? prev.filter(id => id !== threadId)
        : [...prev, threadId]
    );
  };

  const handleMergeThreads = async () => {
    if (selectedThreads.length < 2) {
      toast({
        title: "Select at least 2 threads",
        description: "You need to select at least 2 threads to merge",
        variant: "destructive",
      });
      return;
    }
    
    // Fetch all messages from selected threads
    const threadsData = await Promise.all(
      selectedThreads.map(async (threadId) => {
        const conv = conversations.find(c => c.id === threadId);
        const { data: messages } = await supabase
          .from('giblet_chat_messages')
          .select('*')
          .eq('conversation_id', threadId)
          .order('created_at');
        
        return {
          conversationId: threadId,
          title: conv?.title || 'Untitled',
          messages: messages || [],
          metadata: conv?.metadata || {}
        };
      })
    );
    
    // Auto-generate merged title
    const titles = threadsData.map(t => t.title.split(' ')[0]).join(' + ');
    setMergedThreadTitle(`MERGED - ${titles}`);
    
    setMergePreviewData(threadsData);
    setShowMergePreview(true);
  };

  const getMergedMessages = (threadsData: any[]) => {
    const allMessages = threadsData.flatMap((thread, idx) => 
      thread.messages.map((msg: any) => ({
        ...msg,
        threadColor: THREAD_COLORS[idx % THREAD_COLORS.length],
        threadTitle: thread.title,
        sourceThreadId: thread.conversationId
      }))
    );
    
    return allMessages.sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  };

  const detectDuplicates = (messages: any[]) => {
    const duplicates = new Set<string>();
    
    for (let i = 0; i < messages.length; i++) {
      for (let j = i + 1; j < messages.length; j++) {
        if (
          messages[i].content === messages[j].content &&
          Math.abs(new Date(messages[i].created_at).getTime() - new Date(messages[j].created_at).getTime()) < 3600000
        ) {
          duplicates.add(messages[j].id);
        }
      }
    }
    
    return duplicates;
  };

  const confirmMergeThreads = async () => {
    if (mergingInProgress) {
      return;
    }

    setMergingInProgress(true);

    try {
      // Verify source threads still exist and fetch their metadata
      const { data: existingThreads } = await supabase
        .from('giblet_conversations')
        .select('id, metadata')
        .in('id', selectedThreads);

      if (!existingThreads || existingThreads.length !== selectedThreads.length) {
        toast({
          title: "Merge failed",
          description: "Some selected threads no longer exist",
          variant: "destructive",
        });
        setMergingInProgress(false);
        setShowMergePreview(false);
        setMergeMode(false);
        setSelectedThreads([]);
        return;
      }

      const mergedMessages = getMergedMessages(mergePreviewData);
      const duplicates = detectDuplicates(mergedMessages);
      const uniqueMessages = mergedMessages.filter(msg => !duplicates.has(msg.id));
      
      // Collect all unique KB IDs from source threads
      const allKbIds = new Set<string>();
      existingThreads.forEach(thread => {
        const metadata = thread.metadata as Record<string, any> | null;
        if (metadata?.connected_kb_ids && Array.isArray(metadata.connected_kb_ids)) {
          metadata.connected_kb_ids.forEach((kbId: string) => allKbIds.add(kbId));
        }
      });
      const consolidatedKbIds = Array.from(allKbIds);
      
      // Create new merged conversation with consolidated KB IDs
      const { data: newConv, error: convError } = await supabase
        .from('giblet_conversations')
        .insert({
          user_id: userId,
          title: mergedThreadTitle,
          giblet_variant: currentVariant,
          is_active: true,
          metadata: {
            is_merged: true,
            source_threads: selectedThreads,
            merge_date: new Date().toISOString(),
            connected_kb_ids: consolidatedKbIds,
            thread_colors: mergePreviewData.map((_: any, idx: number) => ({
              title: mergePreviewData[idx].title,
              color: THREAD_COLORS[idx % THREAD_COLORS.length]
            }))
          }
        })
        .select()
        .single();
      
      if (convError) throw convError;
      
      // Copy messages to new conversation
      for (const msg of uniqueMessages) {
        await supabase
          .from('giblet_chat_messages')
          .insert({
            conversation_id: newConv.id,
            user_id: userId,
            role: msg.role,
            content: msg.content,
            connected_kb_ids: msg.connected_kb_ids || [],
            workflow_state: msg.workflow_state || {},
            metadata: {
              source_thread_id: msg.sourceThreadId,
              source_thread_title: msg.threadTitle,
              thread_color: msg.threadColor,
              original_created_at: msg.created_at
            },
            created_at: msg.created_at
          });
      }
      
      // Generate AI summary
      const summaryPrompt = `I've just merged ${selectedThreads.length} conversation threads:
${mergePreviewData.map((t: any) => `- ${t.title}`).join('\n')}

Total messages: ${uniqueMessages.length} (${duplicates.size} duplicates removed)

Please provide a concise summary (2-3 sentences) of the key points across all these conversations.`;
      
      try {
        const summaryResult = await callGibletApi(
          summaryPrompt,
          '',
          uniqueMessages.slice(-10),
          [],
          {}
        );
        
        await supabase
          .from('giblet_chat_messages')
          .insert({
            conversation_id: newConv.id,
            user_id: userId,
            role: 'assistant',
            content: `**📋 Merged Thread Summary**\n\n${summaryResult.response}\n\n---\n\n*This thread was merged from ${selectedThreads.length} conversations. Messages are color-coded by source thread.*`,
            metadata: {
              is_merge_summary: true
            }
          });
      } catch (error) {
        console.error('Error generating summary:', error);
      }
      
      // Mark source threads as merged (retain them in history)
      for (const threadId of selectedThreads) {
        const { data: thread } = await supabase
          .from('giblet_conversations')
          .select('metadata')
          .eq('id', threadId)
          .single();
        
        const existingMetadata = (thread?.metadata || {}) as Record<string, any>;
        
        await supabase
          .from('giblet_conversations')
          .update({
            metadata: {
              ...existingMetadata,
              is_source_thread: true,
              merged_into_thread_id: newConv.id,
              merge_date: new Date().toISOString()
            }
          })
          .eq('id', threadId);
      }
      
      // Reset state and reload
      setMergeMode(false);
      setSelectedThreads([]);
      setShowMergePreview(false);
      setMergePreviewData(null);
      
      toast({
        title: "Synthesis thread created",
        description: `Created "${mergedThreadTitle}" from ${selectedThreads.length} conversations. Original threads preserved.`,
      });
      
      loadConversations();
      onSelectConversation(newConv.id);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error merging threads:', error);
      toast({
        title: "Failed to merge threads",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setMergingInProgress(false);
    }
  };

  const handleUpdateTitle = async (conversationId: string, newTitle: string) => {
    try {
      const { error } = await supabase
        .from('giblet_conversations')
        .update({ title: newTitle })
        .eq('id', conversationId);

      if (error) throw error;

      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId ? { ...conv, title: newTitle } : conv
        )
      );
      setEditingId(null);
      toast({ title: "Conversation renamed" });
    } catch (error: any) {
      toast({
        title: "Failed to rename conversation",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      const { error } = await supabase
        .from('giblet_conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
      toast({ title: "Conversation deleted" });
    } catch (error: any) {
      toast({
        title: "Failed to delete conversation",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupConversationsByDate = (convs: Conversation[]) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    return {
      today: convs.filter(c => new Date(c.last_message_at).toDateString() === today.toDateString()),
      yesterday: convs.filter(c => new Date(c.last_message_at).toDateString() === yesterday.toDateString()),
      lastWeek: convs.filter(c => {
        const date = new Date(c.last_message_at);
        return date < yesterday && date >= lastWeek;
      }),
      older: convs.filter(c => new Date(c.last_message_at) < lastWeek),
    };
  };

  const grouped = groupConversationsByDate(filteredConversations);

  const renderConversation = (conv: Conversation) => {
    const isCallThread = conv.metadata?.is_call_thread === true;
    const cannotMerge = isCallThread;
    
    return (
      <div
        key={conv.id}
        className={`group p-3 rounded-lg transition-colors ${
          mergeMode && !cannotMerge && selectedThreads.includes(conv.id)
            ? 'bg-blue-100'
            : mergeMode
            ? 'hover:bg-accent/30'
            : 'hover:bg-accent/50 cursor-pointer'
        }`}
        onClick={() => {
          if (mergeMode && !cannotMerge) {
            handleThreadSelect(conv.id);
          } else if (!mergeMode) {
            onSelectConversation(conv.id);
            onOpenChange(false);
          }
        }}
      >
        <div className="flex items-start gap-2">
          {mergeMode && (
            <Checkbox
              checked={selectedThreads.includes(conv.id)}
              disabled={cannotMerge}
              onCheckedChange={() => !cannotMerge && handleThreadSelect(conv.id)}
              onClick={(e) => e.stopPropagation()}
              className="mt-1"
            />
          )}
          
          <div className="flex-1 min-w-0">
            {editingId === conv.id ? (
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUpdateTitle(conv.id, editTitle);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  autoFocus
                />
                <Button size="sm" onClick={() => handleUpdateTitle(conv.id, editTitle)}>
                  Save
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm line-clamp-1">{conv.title}</h4>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {isCallThread && (
                        <Badge variant="outline" className="text-xs">
                          <PhoneCall className="h-3 w-3 mr-1" />
                          Call
                        </Badge>
                      )}
                      {conv.metadata?.is_merged && (
                        <Badge variant="secondary" className="text-xs">
                          <Layers className="h-3 w-3 mr-1" />
                          Merged ({conv.metadata.source_threads?.length || 0})
                        </Badge>
                      )}
                      {cannotMerge && mergeMode && (
                        <Badge variant="destructive" className="text-xs">
                          Cannot Merge
                        </Badge>
                      )}
                    </div>
                  </div>
                  {!mergeMode && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(conv.id);
                          setEditTitle(conv.title);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConversationToDelete(conv.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MessageSquare className="h-3 w-3" />
                  <span>{conv.message_count} messages</span>
                  <span>•</span>
                  <span>{formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-80">
          <SheetHeader>
            <SheetTitle>Chat History</SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                onClick={onNewConversation} 
                className="flex-1"
                disabled={mergeMode}
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                NEW
              </Button>
              <Button 
                onClick={() => {
                  if (mergeMode && selectedThreads.length > 0) {
                    setMergeMode(false);
                    setSelectedThreads([]);
                  } else {
                    setMergeMode(!mergeMode);
                  }
                }}
                className="flex-1"
                variant={mergeMode ? "default" : "outline"}
              >
                <GitMerge className="mr-2 h-4 w-4" />
                {mergeMode ? "CANCEL" : "MERGE"}
              </Button>
            </div>

            {mergeMode && selectedThreads.length > 0 && (
              <Button 
                onClick={handleMergeThreads} 
                className="w-full"
                variant="default"
              >
                Merge {selectedThreads.length} Threads
              </Button>
            )}

            <ScrollArea className="h-[calc(100vh-220px)]">
              <div className="space-y-6 pr-4">
                {loading ? (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    Loading conversations...
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    {searchQuery ? 'No conversations found' : 'No conversations yet'}
                  </div>
                ) : (
                  <>
                    {grouped.today.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          Today
                        </h3>
                        <div className="space-y-1">
                          {grouped.today.map(renderConversation)}
                        </div>
                      </div>
                    )}

                    {grouped.yesterday.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          Yesterday
                        </h3>
                        <div className="space-y-1">
                          {grouped.yesterday.map(renderConversation)}
                        </div>
                      </div>
                    )}

                    {grouped.lastWeek.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          Last 7 Days
                        </h3>
                        <div className="space-y-1">
                          {grouped.lastWeek.map(renderConversation)}
                        </div>
                      </div>
                    )}

                    {grouped.older.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          Older
                        </h3>
                        <div className="space-y-1">
                          {grouped.older.map(renderConversation)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and all its messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => conversationToDelete && handleDeleteConversation(conversationToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Merge Preview Dialog */}
      <Dialog open={showMergePreview} onOpenChange={setShowMergePreview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Merge Threads Preview</DialogTitle>
            <DialogDescription>
              Review the merged thread before confirming. Each source thread will be color-coded.
            </DialogDescription>
          </DialogHeader>
          
          {mergePreviewData && (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {mergePreviewData.map((thread: any, idx: number) => (
                  <Badge 
                    key={idx} 
                    style={{ backgroundColor: THREAD_COLORS[idx % THREAD_COLORS.length], color: 'white' }}
                  >
                    {thread.title}
                  </Badge>
                ))}
              </div>
              
              <ScrollArea className="h-96 border rounded p-4">
                {getMergedMessages(mergePreviewData).slice(0, 20).map((msg: any, idx: number) => (
                  <div 
                    key={idx} 
                    className="mb-4 p-3 rounded bg-muted/30"
                    style={{ borderLeft: `4px solid ${msg.threadColor}` }}
                  >
                    <div className="text-xs text-muted-foreground mb-1">
                      From: {msg.threadTitle} • {formatDistance(new Date(msg.created_at), new Date(), { addSuffix: true })}
                    </div>
                    <div className="font-medium mb-1">{msg.role === 'user' ? 'You' : 'Giblet'}</div>
                    <div className="text-sm">{msg.content.substring(0, 200)}{msg.content.length > 200 ? '...' : ''}</div>
                  </div>
                ))}
                {getMergedMessages(mergePreviewData).length > 20 && (
                  <div className="text-center text-sm text-muted-foreground py-2">
                    ...and {getMergedMessages(mergePreviewData).length - 20} more messages
                  </div>
                )}
              </ScrollArea>
              
              <div className="space-y-2">
                <Label>Merged Thread Title</Label>
                <Input
                  value={mergedThreadTitle}
                  onChange={(e) => setMergedThreadTitle(e.target.value)}
                  placeholder="MERGED - Sales + Marketing + Finance"
                />
              </div>
              
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setShowMergePreview(false)}
                  disabled={mergingInProgress}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmMergeThreads}
                  disabled={mergingInProgress}
                >
                  {mergingInProgress ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                      Merging...
                    </>
                  ) : (
                    'Confirm Merge'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
